/**
 * AG-UI Chat Component
 *
 * Default SDC implementation that uses AguiChatCore for logic
 * and provides DOM rendering. This is the "batteries included" version.
 *
 * For custom implementations, use AguiChatCore directly.
 */

import { Message } from '@ag-ui/core';
import { randomUUID } from '@ag-ui/client';
import {
  AguiChatCore,
  getInstance,
  registerInstance,
  unregisterInstance,
  type AguiChatCoreConfig,
  type HitlRequest,
  type HitlResponse,
  type SuggestionPill,
} from './core';
import {
  formatToolName,
  renderMarkdown,
  StatusIndicatorManager,
  type ChatConfig,
  type ChatElements,
  parseChatConfig,
  queryChatElements,
  createMessageElement,
  updateMessageContent,
  scrollToBottom,
  createHitlDialog,
  toolRegistry,
  initializeToolsAPI,
  createDebugLogger,
  type DebugLogger,
} from './utils';

/**
 * Registry of live AguiChat renderer instances, keyed by their container.
 * Lets external code (e.g. a multi-input page) look up the running renderer
 * and retarget() it at a different container while keeping the same core/thread.
 */
const chatInstances = new Map<HTMLElement, AguiChat>();

/**
 * Gets a live AguiChat renderer instance by container element or selector.
 */
function getChatInstance(target: string | HTMLElement): AguiChat | undefined {
  const element = typeof target === 'string'
    ? (document.querySelector(target) as HTMLElement | null)
    : target;
  return element ? chatInstances.get(element) : undefined;
}

/**
 * AG-UI Chat component class - Default DOM renderer.
 *
 * Bridges AguiChatCore with DOM rendering. Consumers who want to customize
 * rendering should use AguiChatCore directly and implement their own renderer.
 */
class AguiChat {
  private core: AguiChatCore;
  private config: ChatConfig;
  private elements: ChatElements;
  private indicators: StatusIndicatorManager;

  // Current message element tracking
  private currentAssistantMessage: HTMLElement | null = null;
  private currentHitlDialog: HTMLElement | null = null;
  private currentHitlRequest: HitlRequest | null = null;
  private currentConversation: HTMLElement | null = null;

  // Custom tool render tracking
  private customRenderedTools = new Set<string>();

  // Suggestions container reference
  private suggestionsContainer: HTMLElement | null = null;

  // Debug logging (timestamped, no-ops unless config.config.debug is enabled)
  private debugLogger: DebugLogger = createDebugLogger(() => this.config.config.debug ?? false);

  // Element listeners are stored as stable instance fields so retarget()
  // can remove them from the old container and add them to the new one.
  // They dereference this.elements at call time, so they always act on the
  // currently targeted elements.
  private onInput = (): void => {
    this.elements.input.value = this.elements.input.textContent;

    if (this.elements.input.value.trim() !== '') {
      this.elements.form[0].classList.add('hidden');
    }
    else {
      this.elements.form[0].classList.remove('hidden');
    }
  };

  private onKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();

      this.elements.form.requestSubmit();
    }
  };

  private onSubmit = (e: Event): void => {
    e.preventDefault();
    this.handleFormSubmit();
  };

  private onDataActionClick = (e: Event): void => {
    const target = (e.target as HTMLElement).closest('[data-agui-action]');
    if (!target) return;

    const action = target.getAttribute('data-agui-action');
    this.executeAction(action, target as HTMLElement);
  };

  private onSuggestionClick = (e: Event): void => {
    const target = e.target as HTMLElement;
    if (target.classList.contains('agui-chat__suggestion-pill')) {
      const message = target.getAttribute('data-message');
      if (message) {
        this.core.sendMessage(message);
      }
    }
  };

  constructor(container: HTMLElement) {
    // Parse configuration from data attributes
    this.config = parseChatConfig(container);

    // Query required DOM elements
    const elements = queryChatElements(container);
    if (!elements) {
      throw new Error('AG-UI Chat: Missing required elements');
    }
    this.elements = elements;

    // Initialize the core
    const coreConfig: AguiChatCoreConfig = {
      endpoint: this.config.endpoint,
      runnerEndpoint: this.config.runnerEndpoint,
      agentId: this.config.agentId,
      tokenEndpoint: this.config.tokenEndpoint,
      debug: this.config.config.debug,
      maxMessages: this.config.config.maxMessages,
      streamTimeout: this.config.config.streamTimeout,
    };
    this.core = new AguiChatCore(coreConfig);

    // Register instance for external access
    registerInstance(container, this.core);
    chatInstances.set(container, this);

    // Initialize status indicator manager
    this.indicators = new StatusIndicatorManager(
      this.elements.messagesContainer,
      () => this.scrollToBottom(),
    );

    // Subscribe to core events and render
    this.setupEventListeners();

    // Bind input/keydown/submit and data-action listeners on the container
    this.bindElementListeners();

    // Set up suggestion pills
    this.setupSuggestions(container);

    // Set up document-level data-attribute actions
    this.setupDataActions();

    // Initialize the core (auth, etc.)
    this.core.initialize();
  }

  /**
   * Binds the element listeners (input/keydown/submit + data-action clicks)
   * to the currently targeted elements.
   */
  private bindElementListeners(): void {
    this.elements.input.addEventListener('input', this.onInput);
    this.elements.input.addEventListener('keydown', this.onKeydown);
    this.elements.form.addEventListener('submit', this.onSubmit);
    this.elements.container.addEventListener('click', this.onDataActionClick);
  }

  /**
   * Removes the element listeners from the currently targeted elements.
   * Called by retarget() before switching containers so the old container
   * goes fully inert instead of manipulating the new one.
   */
  private unbindElementListeners(): void {
    this.elements.input.removeEventListener('input', this.onInput);
    this.elements.input.removeEventListener('keydown', this.onKeydown);
    this.elements.form.removeEventListener('submit', this.onSubmit);
    this.elements.container.removeEventListener('click', this.onDataActionClick);
  }

  /**
   * Sets up event listeners for core events.
   */
  private setupEventListeners(): void {
    // Auth events
    this.core.on('auth:started', () => {
      this.setInputEnabled(false);
      this.updateSystemMessage('Authenticating...');
    });

    this.core.on('auth:completed', () => {
      this.updateSystemMessage('Ready to chat.');
      this.setInputEnabled(true);
    });

    this.core.on('auth:failed', () => {
      this.updateSystemMessage('Authentication failed. Chat may not work with external endpoints.');
      this.setInputEnabled(true);
    });

    this.core.on('ready', () => {
      this.setInputEnabled(true);
    });

    // Chat cleared event - reset the UI
    this.core.on('chat:cleared', () => {
      this.clearUI();
    });

    // Message events
    this.core.on('message:added', ({ message, isUser }) => {
      if (isUser) {
        // When groupConversations is enabled, create a conversation wrapper
        if (this.config.config.groupConversations) {
          this.currentConversation = document.createElement('div');
          this.currentConversation.classList.add('agui-chat__conversation');
          this.elements.messagesContainer.appendChild(this.currentConversation);
        }
        this.displayMessage(message);
      }
    });

    this.core.on('message:updated', ({ messageId, content }) => {
      if (this.currentAssistantMessage) {
        updateMessageContent(this.currentAssistantMessage, content);
        this.scrollToBottom();
      } else {
        // Create element if not exists (for first content)
        const message: Message = {
          id: messageId || randomUUID(),
          role: 'assistant',
          content,
        };
        this.currentAssistantMessage = createMessageElement(message);

        // When groupConversations is enabled and we have an active conversation, append there
        if (this.config.config.groupConversations && this.currentConversation) {
          this.currentConversation.appendChild(this.currentAssistantMessage);
        } else {
          this.elements.messagesContainer.appendChild(this.currentAssistantMessage);
        }
        this.scrollToBottom();
      }
    });

    this.core.on('message:finalized', ({ content }) => {
      if (this.currentAssistantMessage) {
        if (content) {
          updateMessageContent(this.currentAssistantMessage, content);
        } else {
          // Remove empty message bubbles
          this.currentAssistantMessage.remove();
        }
      }
      this.currentAssistantMessage = null;
      // Note: Don't reset currentConversation here - it persists through the entire run
      // and is reset in run:finished
    });

    // Indicator events
    this.core.on('indicator:show', ({ type, id, text }) => {
      switch (type) {
        case 'connecting':
          this.indicators.showConnecting();
          break;
        case 'thinking':
          this.indicators.showThinking();
          break;
        case 'processing':
          this.indicators.showProcessing();
          break;
        case 'tool-call':
          if (id && text) {
            this.indicators.showToolCall(id, text);
          }
          break;
      }
    });

    this.core.on('indicator:hide', ({ type, id }) => {
      switch (type) {
        case 'connecting':
          this.indicators.hideConnecting();
          break;
        case 'thinking':
          this.indicators.hideThinking();
          break;
        case 'processing':
          this.indicators.hideProcessing();
          break;
        case 'tool-call':
          if (id) {
            this.indicators.hideToolCall(id);
          }
          break;
      }
    });

    // Tool events
    this.core.on('tool:started', ({ toolCallId, toolName, hasCustomRenderer, isHitl }) => {
      if (isHitl) {
        // HITL tools handled separately
        return;
      }

      if (hasCustomRenderer) {
        const result = toolRegistry.startToolCall(toolCallId, toolName, {});
        if (result) {
          this.customRenderedTools.add(toolCallId);
          if (!result.isExternal) {
            // When groupConversations is enabled and we have an active conversation, append there
            if (this.config.config.groupConversations && this.currentConversation) {
              this.currentConversation.appendChild(result.container);
            } else {
              this.elements.messagesContainer.appendChild(result.container);
            }
            this.scrollToBottom();
          }
        }
      } else {
        const friendlyName = formatToolName(toolName);
        this.indicators.showToolCall(toolCallId, friendlyName);
      }
    });

    this.core.on('tool:args', ({ toolCallId, args }) => {
      if (this.customRenderedTools.has(toolCallId)) {
        toolRegistry.updateToolCallArgs(toolCallId, args);
        this.scrollToBottom();
      }
    });

    this.core.on('tool:ended', ({ toolCallId, args }) => {
      if (this.customRenderedTools.has(toolCallId)) {
        toolRegistry.updateToolCallArgs(toolCallId, args);
        this.scrollToBottom();
      } else {
        this.indicators.hideToolCall(toolCallId);
      }
    });

    this.core.on('tool:result', ({ toolCallId, result }) => {
      if (this.customRenderedTools.has(toolCallId)) {
        try {
          toolRegistry.completeToolCall(toolCallId, result);
        } catch (error) {
          this.debugLogger.error('Error completing tool render:', error);
          toolRegistry.errorToolCall(toolCallId, String(error));
        }
        this.scrollToBottom();
      }
    });

    // HITL events
    this.core.on('hitl:request', ({ request }) => {
      this.indicators.clearAll();
      this.removeHitlDialog();
      this.currentHitlRequest = request;

      this.currentHitlDialog = createHitlDialog(request, (response) => {
        this.handleHitlResponse(response);
      });

      this.elements.messagesContainer.appendChild(this.currentHitlDialog);
      this.scrollToBottom();
    });

    // Run events
    this.core.on('run:started', () => {
      this.setInputEnabled(false);
    });

    this.core.on('run:finished', () => {
      this.indicators.clearAll();
      this.customRenderedTools.clear();
      this.currentConversation = null;
      this.setInputEnabled(true);
      this.elements.input.focus();
    });

    this.core.on('run:stopped', ({ messageId }) => {
      this.indicators.clearAll();
      this.customRenderedTools.clear();
      this.currentConversation = null;
      this.setInputEnabled(true);
      this.elements.input.focus();

      // Mark the last assistant message as incomplete so the UI layer can style it
      if (messageId) {
        const message = this.elements.messagesContainer.querySelector(
          `.agui-chat__message--assistant:last-of-type`,
        );
        if (message) {
          message.classList.add('agui-chat__message--stopped');
        }
      }
    });

    this.core.on('run:error', ({ message }) => {
      this.indicators.clearAll();
      this.removeHitlDialog();
      this.customRenderedTools.clear();
      this.currentConversation = null;
      this.displaySystemError(message);
      this.setInputEnabled(true);
      this.elements.input.focus();
    });
  }

  /**
   * Handles form submission.
   */
  private handleFormSubmit(): void {
    const text = this.elements.input.value.trim();
    const inputPlaceholder = this.elements.form[0];
    if (!text || !inputPlaceholder) return;

    this.elements.input.value = '';
    this.elements.input.textContent = '';

    if (inputPlaceholder.classList.contains('hidden')) {
      inputPlaceholder.classList.remove('hidden');
    }

    this.core.sendMessage(text);
  }

  /**
   * Sets up suggestion pill click handlers and syncs initial pills to core.
   */
  private setupSuggestions(container: HTMLElement): void {
    this.suggestionsContainer = container.querySelector('.agui-chat__suggestions') as HTMLElement | null;

    // Sync initial pills from server-rendered DOM to the core.
    if (this.suggestionsContainer) {
      const initialPills: SuggestionPill[] = [];
      this.suggestionsContainer.querySelectorAll('.agui-chat__suggestion-pill').forEach((el) => {
        const title = el.textContent?.trim() || '';
        const message = el.getAttribute('data-message') || '';
        if (title && message) {
          initialPills.push({ title, message });
        }
      });
      if (initialPills.length > 0) {
        // Set pills on core without triggering a re-render (pills are already in the DOM).
        this.core.getPills().length === 0 && this.core.setPills(initialPills);
      }

      this.suggestionsContainer.addEventListener('click', this.onSuggestionClick);
    }

    // Listen for programmatic pill updates and re-render the DOM.
    this.core.on('pills:updated', ({ pills }) => {
      this.renderPills(pills);
    });
  }

  /**
   * Renders suggestion pills into the DOM.
   *
   * Always targets the currently active container (this.elements.container),
   * not whatever container was active when this was first set up - so a
   * pill update (e.g. from a tool call) shows up wherever retarget() last
   * pointed the renderer, not the box it started in.
   */
  private renderPills(pills: SuggestionPill[]): void {
    if (pills.length === 0) {
      // Remove the suggestions container if no pills.
      if (this.suggestionsContainer) {
        this.suggestionsContainer.remove();
        this.suggestionsContainer = null;
      }
      return;
    }

    // Create container if it doesn't exist.
    if (!this.suggestionsContainer) {
      this.suggestionsContainer = document.createElement('div');
      this.suggestionsContainer.classList.add('agui-chat__suggestions');

      // Insert before the input form.
      const form = this.elements.container.querySelector('.agui-chat__input-form');
      if (form) {
        this.elements.container.insertBefore(this.suggestionsContainer, form);
      } else {
        this.elements.container.appendChild(this.suggestionsContainer);
      }

      // Attach click handler to the new container.
      this.suggestionsContainer.addEventListener('click', this.onSuggestionClick);
    }

    // Clear existing pill buttons safely.
    while (this.suggestionsContainer.firstChild) {
      this.suggestionsContainer.removeChild(this.suggestionsContainer.firstChild);
    }

    // Create new pill buttons.
    pills.forEach((pill) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.classList.add('agui-chat__suggestion-pill');
      button.setAttribute('data-message', pill.message);
      button.textContent = pill.title;
      this.suggestionsContainer!.appendChild(button);
    });
  }

  /**
   * Sets up data-attribute based actions.
   * Allows external elements to trigger chat actions via data attributes.
   *
   * @example
   * <button data-agui-action="clear">Clear Chat</button>
   * <button data-agui-action="send" data-agui-message="Hello!">Say Hello</button>
   */
  private setupDataActions(): void {
    // Listen for global actions targeting this chat. Clicks within the
    // container itself are handled by onDataActionClick, which is bound in
    // bindElementListeners() so it moves along on retarget(). The selector
    // match here reads this.elements at call time for the same reason.
    document.addEventListener('click', (e) => {
      const target = (e.target as HTMLElement).closest('[data-agui-action][data-agui-target]');
      if (!target) return;

      const targetSelector = target.getAttribute('data-agui-target');
      if (!targetSelector) return;

      // Check if this action targets our container
      const targetElement = document.querySelector(targetSelector);
      if (targetElement === this.elements.container || this.elements.container.matches(targetSelector)) {
        const action = target.getAttribute('data-agui-action');
        this.executeAction(action, target as HTMLElement);
      }
    });
  }

  /**
   * Executes a data-attribute action.
   */
  private executeAction(action: string | null, element: HTMLElement): void {
    if (!action) return;

    switch (action) {
      case 'clear':
        this.clearChat();
        break;

      case 'send': {
        const message = element.getAttribute('data-agui-message');
        if (message) {
          this.core.sendMessage(message);
        }
        break;
      }

      case 'focus':
        this.elements.input.focus();
        break;

      default:
        this.debugLogger.warn(`AG-UI Chat: Unknown action "${action}"`);
    }
  }

  /**
   * Clears the chat session.
   * Delegates to core which will emit chat:cleared if clearUI is true.
   *
   * @param clearUI - Whether to also clear the UI (default: true)
   */
  private clearChat(clearUI = true): void {
    this.core.clearChat(clearUI);
  }

  /**
   * Clears the chat UI (messages, indicators, dialogs).
   * Called automatically when core emits chat:cleared.
   */
  private clearUI(): void {
    // Clear messages container (keep system message if any)
    const messages = this.elements.messagesContainer.querySelectorAll(
      '.agui-chat__message:not(.agui-chat__message--system)'
    );
    messages.forEach((msg) => msg.remove());

    // Also remove any conversation wrappers
    const conversations = this.elements.messagesContainer.querySelectorAll(
      '.agui-chat__conversation'
    );
    conversations.forEach((conv) => conv.remove());

    // Also remove any tool renders
    const toolRenders = this.elements.messagesContainer.querySelectorAll(
      '.agui-chat__tool-render'
    );
    toolRenders.forEach((el) => el.remove());

    this.currentAssistantMessage = null;
    this.currentConversation = null;
    this.removeHitlDialog();
    this.indicators.clearAll();
    this.customRenderedTools.clear();

    // Reset system message
    this.updateSystemMessage('Ready to chat.');
  }

  /**
   * Handles HITL response from the dialog.
   */
  private handleHitlResponse(response: HitlResponse): void {
    if (!this.currentHitlRequest) return;

    this.removeHitlDialog();
    this.core.respondToHitl(response, this.currentHitlRequest);
    this.currentHitlRequest = null;
  }

  /**
   * Updates the system message in the chat.
   */
  private updateSystemMessage(message: string): void {
    const systemMessage = this.elements.messagesContainer.querySelector('.agui-chat__message--system');
    if (systemMessage) {
      systemMessage.textContent = message;
    }
  }

  /**
   * Displays a system error message.
   */
  private displaySystemError(message: string): void {
    const errorMessage: Message = {
      id: randomUUID(),
      role: 'system',
      content: `Error: ${message}`,
    };
    this.displayMessage(errorMessage);
  }

  /**
   * Displays a message in the chat.
   */
  private displayMessage(message: Message): void {
    const messageEl = createMessageElement(message);

    // When groupConversations is enabled and we have an active conversation, append there
    if (this.config.config.groupConversations && this.currentConversation) {
      this.currentConversation.appendChild(messageEl);
    } else {
      this.elements.messagesContainer.appendChild(messageEl);
    }
    this.scrollToBottom();
  }

  /**
   * Scrolls the messages container to the bottom.
   * Respects the scrollToBottom config option.
   */
  private scrollToBottom(): void {
    if (this.config.config.scrollToBottom) {
      scrollToBottom(this.elements.messagesContainer);
    }
  }

  /**
   * Enables or disables the input field.
   */
  private setInputEnabled(enabled: boolean): void {
    this.elements.input.disabled = !enabled;
  }

  /**
   * Removes the current HITL dialog if present.
   */
  private removeHitlDialog(): void {
    if (this.currentHitlDialog) {
      this.currentHitlDialog.remove();
      this.currentHitlDialog = null;
    }
  }

  /**
   * Gets the core instance for programmatic access.
   */
  getCore(): AguiChatCore {
    return this.core;
  }

  /**
   * Releases this renderer's hold on its current container: removes the
   * element listeners and suggestion click handler, and drops the container
   * from both instance registries. The container's rendered messages are
   * left in place - it becomes an inert transcript.
   */
  private detachFromContainer(): void {
    this.unbindElementListeners();
    if (this.suggestionsContainer) {
      this.suggestionsContainer.removeEventListener('click', this.onSuggestionClick);
    }
    chatInstances.delete(this.elements.container);
    unregisterInstance(this.elements.container);
  }

  /**
   * Takes ownership of a container's elements: registers this instance for
   * it (both registries), repoints the indicator manager, adopts or creates
   * the suggestions container with the core's current pills, and binds the
   * element listeners.
   */
  private attachToElements(elements: ChatElements): void {
    this.elements = elements;
    chatInstances.set(elements.container, this);
    registerInstance(elements.container, this.core);
    // Mark as initialized so a later behaviors attach doesn't spin up a
    // second, independent chat on this container.
    elements.container.setAttribute('data-agui-initialized', 'true');

    this.indicators.setContainer(elements.messagesContainer);

    // Pills are conversation state, not per-box furniture: the active box
    // shows the core's current pill set. Adopt the box's suggestions
    // element if it has one, then re-render from the core.
    this.suggestionsContainer = elements.container.querySelector('.agui-chat__suggestions') as HTMLElement | null;
    if (this.suggestionsContainer) {
      this.suggestionsContainer.addEventListener('click', this.onSuggestionClick);
    }
    this.renderPills(this.core.getPills());

    this.bindElementListeners();
    this.setInputEnabled(!this.core.running);
  }

  /**
   * Moves this chat to a different container, keeping the same core
   * (thread, history, in-flight state).
   *
   * Rendered messages stay in their original container: each box keeps an
   * independent transcript, while the thread's history is shared through
   * the core. New messages render into the new container. A retarget
   * during an active run lets the in-flight response finish in the old
   * box; the next exchange starts in the new one.
   *
   * All element listeners move, so the old container goes fully inert and
   * repeated retargets (including back to the original container) never
   * accumulate duplicate listeners. Suggestion pills follow the chat: the
   * core's current pill set renders into the new box.
   *
   * The target must not host another live chat instance.
   *
   * Caveat: a run stopped after a mid-run retarget styles the last
   * assistant message in the current container only - a partial response
   * left in a previous box is not marked as stopped.
   *
   * @param container - The new container to render into. Must contain the
   *   required chat markup (.agui-chat__messages, .agui-chat__input-form,
   *   .agui-chat__input).
   * @return Whether the retarget succeeded (false if required elements are
   *   missing or the target already hosts another chat).
   */
  retarget(container: HTMLElement): boolean {
    if (container === this.elements.container) {
      return true;
    }

    const occupant = chatInstances.get(container);
    if (occupant) {
      console.warn('AG-UI Chat: Target container already hosts a live chat instance.');
      return false;
    }

    const elements = queryChatElements(container);
    if (!elements) {
      return false;
    }

    this.detachFromContainer();
    this.attachToElements(elements);

    return true;
  }
}

/**
 * Initializes chat components within a context.
 * Tracks initialized elements to avoid double-initialization.
 */
function initializeChats(context: Document | HTMLElement = document): void {
  // Initialize the global tools API on first run
  initializeToolsAPI();

  const chats = context.querySelectorAll('.agui-chat:not([data-agui-initialized])');
  chats.forEach((chat) => {
    try {
      new AguiChat(chat as HTMLElement);
      chat.setAttribute('data-agui-initialized', 'true');
    } catch (error) {
      console.error('Failed to initialize AG-UI Chat:', error);
    }
  });
}

// Drupal behaviors for AJAX compatibility
if (typeof window.Drupal !== 'undefined' && window.Drupal.behaviors) {
  window.Drupal.behaviors.aguiChat = {
    attach: (context: Document | HTMLElement) => {
      initializeChats(context);
    },
  };
} else {
  // Fallback for non-Drupal environments
  document.addEventListener('DOMContentLoaded', () => initializeChats());
}

// Export for external use
export { AguiChat, AguiChatCore, getInstance, getChatInstance, type SuggestionPill };

// Expose global API for programmatic access
// Note: esbuild's IIFE format with --global-name assigns exports to window.AguiChat
// AFTER all module code runs, so we need to defer our API setup
if (typeof window !== 'undefined') {
  // Use setTimeout(0) to ensure this runs after esbuild's IIFE assignment
  setTimeout(() => {
    // Create API object
    const api = {
      /**
       * Get a chat core instance by selector or element.
       * @example
       * const chat = window.AguiChat.getInstance('#my-chat');
       * chat.sendMessage('Hello!');
       */
      getInstance,

      /**
       * Get the live renderer instance (DOM wrapper) by its container
       * element or selector. Use retarget() on the result to move a
       * running renderer to a different container while keeping the same
       * core/thread. Rendered messages stay in their original box; new
       * messages render in the new one.
       * @example
       * const chat = window.AguiChat.getChatInstance('.agui-chat');
       * chat.retarget(document.querySelector('#other-box'));
       */
      getChatInstance,

      /**
       * Core class for creating custom chat implementations.
       * @example
       * const core = new window.AguiChat.Core({ endpoint: '/api/chat' });
       * core.on('message:added', (msg) => console.log(msg));
       * await core.initialize();
       */
      Core: AguiChatCore,

      /**
       * Default chat component class (used by SDC).
       */
      Chat: AguiChat,

      /**
       * Render markdown text to sanitized HTML.
       * Uses markdown-it with DOMPurify sanitization.
       * Exposed so external modules (e.g. chat-system history restore)
       * can render stored markdown content identically to live messages.
       */
      renderMarkdown,
    };

    // Assign to window
    (window as Window & { AguiChat?: typeof api }).AguiChat = api;

    // Dispatch event so external code knows the API is ready
    window.dispatchEvent(new CustomEvent('agui:chat:ready', { detail: { api } }));
  }, 0);
}

// Global type declarations
declare global {
  interface Window {
    Drupal?: {
      behaviors: Record<string, {
        attach?: (context: Document | HTMLElement, settings?: unknown) => void;
        detach?: (context: Document | HTMLElement, settings?: unknown, trigger?: string) => void;
      }>;
    };
    AguiChat?: {
      getInstance: typeof getInstance;
      getChatInstance: typeof getChatInstance;
      Core: typeof AguiChatCore;
      Chat: typeof AguiChat;
      renderMarkdown: typeof renderMarkdown;
    };
  }
}

